import './app.css'
import Loading from '@mortgage-pos/ui/components/Loading'
import { Routes, Route } from 'react-router-dom'
import { importRetry } from '@mortgage-pos/ui/utils/importRetry'
import Footer from '@mortgage-pos/ui/components/Footer'
import { useVariation } from '@mortgage-pos/ui/services/featureFlags'
import { FEATURE_FLAG_TYPES } from '@mortgage-pos/data'
import React, { Suspense, lazy, useEffect, useState, ReactNode } from 'react'
import { useStoreActions } from './store'
import useLocalHead from '@shared-fe/hooks/useLocalHead'
import { scssManifest } from '../styles/data/filesManifest'
import useMediaQuery from '@mortgage-pos/ui/hooks/useMediaQuery'
import { ModalProvider } from '@mortgage-pos/ui/components/Modal'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import useCheckAuthentication from '@mortgage-pos/ui/hooks/useCheckAuthentication'
import { isInIframe } from './utils/iframeCheck'
import RouteTransition from './components/RouteTransition'

// App level components
const SectionWrapper = React.memo(({ children }: { children: ReactNode }) => (
  <section className="app-content__wrap">{children}</section>
))

const SignInButton = lazy(() =>
  importRetry(() => import('@mortgage-pos/ui/components/SignInButton'))
)

const AppHeader = lazy(() =>
  importRetry(() => import('@mortgage-pos/ui/components/AppHeader'))
)

const AppSidebar = lazy(() =>
  importRetry(() => import('@mortgage-pos/ui/components/AppSidebar'))
)

const ValuePropsModal = lazy(() =>
  importRetry(() => import('@mortgage-pos/ui/components/ValuePropsModal'))
)

// page imports
const ErrorPage = lazy(() =>
  importRetry(() => import('@mortgage-pos/ui/pages/Error'))
)

const SummaryPage = lazy(() =>
  importRetry(() => import('@mortgage-pos/ui/pages/Review'))
)

const RateSelection = lazy(() =>
  importRetry(() => import('@mortgage-pos/ui/pages/RateSelection'))
)

const NoRates = lazy(() =>
  importRetry(() => import('@mortgage-pos/ui/pages/NoRates'))
)

const ThankYou = lazy(() =>
  importRetry(() => import('@mortgage-pos/ui/pages/ThankYou'))
)

const Questionnaire = lazy(() =>
  importRetry(() => import('@mortgage-pos/ui/pages/Questionnaire'))
)

const Success = lazy(() =>
  importRetry(() => import('@mortgage-pos/ui/pages/SuccessScreen'))
)

const SuccessConfetti = lazy(() =>
  importRetry(() => import('@mortgage-pos/ui/pages/SuccessConfettiScreen'))
)

const Continue = lazy(() =>
  importRetry(() => import('@mortgage-pos/ui/pages/ContinueLoginScreen'))
)

const Login = lazy(() =>
  importRetry(() => import('@mortgage-pos/ui/pages/LoginRedirectPage'))
)

const OneTrustDNSAgreement = lazy(() =>
  importRetry(() => import('@mortgage-pos/ui/pages/OneTrustDNSAgreement'))
)

const Prequal = lazy(() =>
  importRetry(() => import('@mortgage-pos/ui/pages/Prequal'))
)

const BankrateModal = lazy(() =>
  importRetry(() => import('./components/BankrateModal'))
)

const routes = [
  {
    path: '/',
    element: Questionnaire,
  },
  {
    path: '/error',
    element: ErrorPage,
    withWrapper: true,
  },
  {
    path: '/rate-select',
    element: RateSelection,
  },
  {
    path: '/no-rates',
    element: NoRates,
    withWrapper: true,
  },
  {
    path: '/thank-you',
    element: ThankYou,
    withWrapper: true,
  },
  {
    path: '/success',
    element: Success,
    withWrapper: true,
  },
  {
    path: '/continue',
    element: Continue,
  },
  {
    path: '/login',
    element: Login,
  },
  {
    path: '/summary',
    element: SummaryPage,
  },
  {
    path: '/completed',
    element: SuccessConfetti,
  },
  {
    path: '/contact-loan-officer',
    element: ThankYou,
    withWrapper: true,
  },
  {
    path: '/prequal',
    element: Prequal,
  },
  {
    path: '/onetrust-dns-agreement',
    element: OneTrustDNSAgreement,
  },
]

export const AppNtl = () => {
  useCheckAuthentication()

  const alfalfaChromeExt = useStoreActions((store) => store.alfalfaChromeExt)

  const urlParams = new URLSearchParams(window.location.search)
  const questionSet = urlParams.get('question-set')
  const bankrateLeadIdParam = urlParams.get('bankrate-lead-id')
  const valuePropQuestionSets = useVariation(
    FEATURE_FLAG_TYPES.VALUE_PROPS_MODAL_QUESTION_SETS
  )

  const bankrateModalQuestionSets = useVariation(
    FEATURE_FLAG_TYPES.BANKRATE_MODAL_QUESTION_SETS
  )

  const bankrateCompareEnabled = useVariation(
    FEATURE_FLAG_TYPES.BANKRATE_COMPARE_ENABLED
  )

  const skipBorrowerPortal = useVariation(
    FEATURE_FLAG_TYPES.SKIP_BORROWER_PORTAL
  )

  const sidebarEnabled = useVariation(FEATURE_FLAG_TYPES.SIDEBAR_ENABLED, true)

  const showValuePropModal = valuePropQuestionSets?.includes(questionSet)

  const [showBankrateModal, setShowBankrateModal] = useState(false)

  // Initialize Alfalfa chrome extension handlers
  useEffect(() => {
    window.addEventListener('message', alfalfaChromeExt.handleAlfalfaEvent)

    // When called, this fn logs the alfalfa event stack
    ;(window as any).logAlfalfaEvents = alfalfaChromeExt.logAlfalfaEvents

    return () => {
      window.removeEventListener('message', alfalfaChromeExt.handleAlfalfaEvent)
    }
  })

  useEffect(() => {
    if (bankrateModalQuestionSets?.includes(questionSet)) {
      setShowBankrateModal(true)
    }
  }, [bankrateModalQuestionSets, questionSet])

  useLocalHead({
    scssManifest,
    uiProjectName: 'ui',
    productName: process.env.PRODUCT_NAME,
  })

  const onCloseBankrateModal = () => {
    setShowBankrateModal(false)
  }

  const HeaderOrSidebar = useMediaQuery(
    '(min-width: 950px)',
    AppSidebar,
    AppHeader
  )

  return (
    <Suspense fallback={<Loading />}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <React.StrictMode>
          <ModalProvider>
            <div
              className={`app-shell ${
                !sidebarEnabled ? '--header-always-shown' : ''
              }`}
              data-testid="app-1003"
            >
              {showValuePropModal ? <ValuePropsModal /> : null}

              {isInIframe() ? null : sidebarEnabled ? (
                <HeaderOrSidebar />
              ) : (
                <AppHeader />
              )}

              {!!bankrateLeadIdParam && showBankrateModal ? (
                <BankrateModal onClose={onCloseBankrateModal} />
              ) : null}

              {bankrateCompareEnabled || skipBorrowerPortal ? null : (
                <div className="app-sign-in">
                  <SignInButton />
                </div>
              )}

              <main
                className={`app-content ${
                  !sidebarEnabled ? '--no-sidebar' : ''
                }`}
              >
                <Suspense fallback={<Loading />}>
                  <Routes>
                    {routes.map(
                      ({ withWrapper, element: Element, ...routeProps }) => (
                        <Route
                          key={routeProps.path}
                          {...routeProps}
                          element={
                            withWrapper ? (
                              <SectionWrapper>
                                <Element />
                              </SectionWrapper>
                            ) : (
                              <Element />
                            )
                          }
                        />
                      )
                    )}
                  </Routes>
                </Suspense>
                <Footer />
              </main>
            </div>
          </ModalProvider>
        </React.StrictMode>
      </LocalizationProvider>
    </Suspense>
  )
}

export default AppNtl
